import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  clearAuthData,
  canDeletePondDetail,
  canEditSamplingFishData,
  getUserDetails,
  isJwtAuthFailure,
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import PondWorkflowTabs from "../../components/custom/PondWorkflowTabs";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import {
  formatDisplayDate as formatDateForMobile,
  formatDisplayDateTime as formatDateTime,
  formatFishingSamplingFileLabel,
} from "../../utils/formatDisplayDate";

const PondDetailViaPond = () => {
  const [pondDetails, setPondDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [entriesPerPage] = useState(10);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [fishSamples, setFishSamples] = useState({});
  const tableBorderColor = "var(--fd-border-table)";
  const cellHeading = { padding: "15px 12px", fontWeight: "600", color: "var(--fd-text-heading)", fontSize: "0.9rem", borderRight: "none", borderLeft: "none" };
  const cellBody = { padding: "15px 12px", fontWeight: "500", color: "var(--fd-text)", border: "none" };
  const cellMuted = { padding: "15px 12px", color: "var(--fd-text-muted)", fontSize: "0.9rem", border: "none" };
  const canEditSampling = canEditSamplingFishData(getUserDetails());
  const canDeleteSampling = canDeletePondDetail(getUserDetails());
  const { id } = useParams();

  const formatWrAsPercent = (wr) => {
    if (wr === "" || wr === undefined || wr === null) return "";
    const num = parseFloat(wr);
    if (Number.isNaN(num)) return "";
    return (num * 100).toFixed(1) + "%";
  };

  const FetchPondDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/PondDetail/GetAllDetailsByPondId/${id}`, {
        params: { search: searchTerm },
      });
      const body = response.data;
      setPondDetails(Array.isArray(body) ? body : body?.data ?? []);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        showErrorToast(error.response?.data?.message || "Failed to fetch pond details");
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, searchTerm]);

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  const FetchFishSamples = useCallback(async (pondDetailsList) => {
    const fishDataObj = {};
    await Promise.all(
      pondDetailsList.map(async (detail) => {
        try {
          const res = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${detail.id}`);
          const body = res.data;
          fishDataObj[detail.id] = Array.isArray(body) ? body : body?.data ?? [];
        } catch {
          fishDataObj[detail.id] = [];
        }
      })
    );
    setFishSamples(fishDataObj);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    checkTokenValidity();
    CheckExpiry();
    FetchPondDetail();
  }, [FetchPondDetail, CheckExpiry]);

  useEffect(() => {
    if (pondDetails.length > 0) {
      FetchFishSamples(pondDetails);
    } else {
      setFishSamples({});
    }
  }, [pondDetails, FetchFishSamples]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== undefined) {
        FetchPondDetail();
      }
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, FetchPondDetail]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = pondDetails.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(pondDetails.length / entriesPerPage) || 1;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const DeletePond = async (detailId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);
    try {
      await axiosInstance.delete(`/api/PondDetail/DeletePondDetail/${detailId}`);
      setPondDetails((prev) => prev.filter((pond) => pond.id !== detailId));
      showSuccessToast("Pond detail deleted successfully");
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        showErrorToast(error.response?.data?.message || "Failed to delete pond detail");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && pondDetails.length === 0) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-workflow-page donezo-fishing-data py-3">
        <div className="container-fluid px-3 px-md-4">
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
            <div className="mb-3 mb-md-0">
              <ul className="breadcrumb mb-2 small">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/pond">Pond</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">Fishing / Sampling Data</li>
              </ul>
              <h1 className="mb-0 page-header donezo-page-header-title d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-3 text-white shadow-sm flex-shrink-0"
                  style={{
                    width: 52,
                    height: 52,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  }}
                  aria-hidden
                >
                  <i className="fas fa-chart-line" style={{ fontSize: '1.35rem' }} />
                </span>
                Fishing / Sampling Data
              </h1>
            </div>
            <div>
              {canEditSampling && (
                <Link
                  to={"/createpd/" + id}
                  className="btn btn-success px-4 py-2 rounded-pill shadow-sm"
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    backgroundColor: '#28a745',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <i className="fa fa-plus me-2"></i>
                  {windowWidth > 576 ? "Add Fishing / Sampling Data" : "Add"}
                </Link>
              )}
            </div>
          </div>

          {/* Navigation Tabs */}
          <PondWorkflowTabs pondId={id} active="sampling" />

          {/* Search Bar */}
          <div className="card border-0 shadow-sm mb-4 donezo-workflow-card">
            <div className="card-body p-3">
              <div className="position-relative">
                <div
                  className="position-absolute top-50 start-0 translate-middle-y text-muted"
                  style={{ zIndex: 10, paddingLeft: '15px' }}
                >
                  <i className="fa fa-search"></i>
                </div>
                <input
                  type="search"
                  className="form-control donezo-fishing-data__search-input w-100"
                  placeholder="Search Pond Detail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* Data Table Card */}
          <div className="card border-0 shadow-sm donezo-workflow-card">
            <div className="card-body p-3 p-md-4">

              {/* Desktop: column header + one bordered card per fishing/sampling event */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table align-middle mb-3" style={{ tableLayout: "fixed", width: "100%" }}>
                    <colgroup>
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "26%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "18%" }} />
                    </colgroup>
                    <thead>
                      <tr style={{ backgroundColor: "var(--fd-bg-thead)", borderBottom: `1px solid ${tableBorderColor}` }}>
                        <th style={cellHeading}>Pond</th>
                        <th style={cellHeading}>Fishing / Sampling File</th>
                        <th className="text-center" style={cellHeading}>Number of Fish</th>
                        <th className="text-center" style={cellHeading}>Fish Species</th>
                        <th style={cellHeading}>Updated At</th>
                        <th style={cellHeading}>Actions</th>
                      </tr>
                    </thead>
                  </table>
                  {currentEntries.map((report, index) => (
                    <div
                      key={report.id}
                      className={`donezo-fishing-data__event-block${index % 2 === 1 ? " is-alt" : ""}`}
                    >
                      <table className="table table-hover align-middle mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
                        <colgroup>
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "26%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "18%" }} />
                          <col style={{ width: "18%" }} />
                        </colgroup>
                        <tbody>
                          <tr
                            onClick={() => canEditSampling && (window.location.href = `/UpdatePD/${report.id}`)}
                            style={{
                              cursor: canEditSampling ? "pointer" : "default",
                              backgroundColor: "transparent",
                              transition: "all 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (canEditSampling) {
                                e.currentTarget.style.backgroundColor = "var(--fd-hover)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            <td style={cellBody}>{report.PondName}</td>
                            <td style={cellBody}>{formatFishingSamplingFileLabel(report)}</td>
                            <td className="text-center" style={{ ...cellBody, fontWeight: "400" }}>{report.NoFishes}</td>
                            <td className="text-center" style={{ ...cellBody, fontWeight: "400" }}>{report.NoFishTypes}</td>
                            <td style={cellMuted}>{formatDateTime(report.updatedAt)}</td>
                            <td onClick={(e) => e.stopPropagation()} style={{ padding: "15px 12px", border: "none" }}>
                              <div className="donezo-row-actions">
                                <Link
                                  to={"/UpdatePD/" + report.id}
                                  className="btn btn-sm btn-primary"
                                  title="Edit Pond Detail"
                                  aria-label="Edit pond detail"
                                >
                                  <i className="fa fa-edit" aria-hidden />
                                </Link>
                                {canDeleteSampling && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => DeletePond(report.id)}
                                    title="Delete Pond Detail"
                                    aria-label="Delete pond detail"
                                  >
                                    <i className="fa fa-trash" aria-hidden />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div className={`donezo-fishing-data__species-panel${index % 2 === 1 ? " is-alt" : ""}`}>
                        <div className="donezo-fishing-data__species-inner">
                          <h6 className="donezo-fishing-data__species-title">
                            <i className="fa fa-fish me-2 text-info"></i>Fish Species Details
                          </h6>
                          <table
                            className="table table-sm mb-0"
                            style={{
                              background: "var(--fd-bg-inner)",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <thead>
                              <tr style={{ backgroundColor: "var(--fd-bg-thead)", borderBottom: `1px solid ${tableBorderColor}` }}>
                                <th style={{ borderTopLeftRadius: "6px", padding: "12px 8px", fontWeight: "600", fontSize: "0.85rem", color: "var(--fd-text)", borderRight: "none", borderLeft: "none" }}>Fish Species</th>
                                <th className="text-center" style={{ padding: "12px 8px", fontWeight: "600", fontSize: "0.85rem", color: "var(--fd-text)", borderRight: "none", borderLeft: "none" }}>Caught</th>
                                <th className="text-center" style={{ padding: "12px 8px", fontWeight: "600", fontSize: "0.85rem", color: "var(--fd-text)", borderRight: "none", borderLeft: "none" }}>Culled</th>
                                <th className="text-center" style={{ padding: "12px 8px", fontWeight: "600", fontSize: "0.85rem", color: "var(--fd-text)", borderRight: "none", borderLeft: "none" }}>Released</th>
                                <th className="text-center" style={{ borderTopRightRadius: "6px", padding: "12px 8px", fontWeight: "600", fontSize: "0.85rem", color: "var(--fd-text)", borderRight: "none", borderLeft: "none" }}>Avg. Wr %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fishSamples[report.id] && fishSamples[report.id].length > 0 ? (
                                fishSamples[report.id]
                                  .filter((fish) => fish.NumberOfFishes > 0)
                                  .map((fish, idx) => (
                                    <tr
                                      key={fish.FishTypeID || idx}
                                      style={{
                                        backgroundColor: idx % 2 === 0 ? "var(--fd-bg-row-even)" : "var(--fd-bg-row-odd)",
                                        transition: "background-color 0.2s ease",
                                        border: "none",
                                      }}
                                    >
                                      <td style={{ padding: "10px 8px", fontWeight: "500", color: "var(--fd-text)", border: "none" }}>{fish.FishTypeName}</td>
                                      <td className="text-center" style={{ padding: "10px 8px", color: "var(--fd-text)", border: "none" }}>
                                        {fish.NumberOfFishes}
                                      </td>
                                      <td className="text-center" style={{ padding: "10px 8px", color: "var(--fd-text)", border: "none" }}>
                                        {fish.NumberOfFishesCulled}
                                      </td>
                                      <td className="text-center" style={{ padding: "10px 8px", color: "var(--fd-text)", border: "none" }}>
                                        {fish.NumberOfFishesReleased}
                                      </td>
                                      <td className="text-center" style={{ padding: "10px 8px", color: "var(--fd-text)", border: "none" }}>
                                        {formatWrAsPercent(fish.AverageWr)}
                                      </td>
                                    </tr>
                                  ))
                              ) : (
                                <tr style={{ backgroundColor: "var(--fd-bg-empty)", border: "none" }}>
                                  <td colSpan={5} className="text-center" style={{ padding: "20px", color: "var(--fd-text-muted)", fontStyle: "italic", border: "none" }}>
                                    No fish data
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="d-md-none">
                {currentEntries.map((report) => (
                  <div key={report.id} className="card mb-3 shadow-sm donezo-workflow-mobile-card">
                    <div
                      className="card-body p-3"
                      onClick={() => canEditSampling && (window.location.href = `/UpdatePD/${report.id}`)}
                      style={{ cursor: canEditSampling ? 'pointer' : 'default' }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="card-title mb-0 h6 fw-semibold">{formatFishingSamplingFileLabel(report)}</h5>
                        {canDeleteSampling && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              DeletePond(report.id);
                            }}
                            title="Delete Pond Detail"
                            aria-label="Delete pond detail"
                          >
                            <i className="fa fa-trash" aria-hidden />
                          </button>
                        )}
                      </div>
                      <div className="row mt-2">
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Pond:</small>
                            <span className="fw-medium">{report.PondName}</span>
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Air Temp:</small>
                            <span className="fw-medium">{report.AirTemperature}</span>
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Sky Climate:</small>
                            <span className="fw-medium">{report.SkyClimate}</span>
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Wind Speed:</small>
                            <span className="fw-medium">{report.WindSpeed}</span>
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Moon Phase:</small>
                            <span className="fw-medium">{report.MoonPhase}</span>
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Barometric:</small>
                            <span className="fw-medium">{report.BaroMetricPressure}</span>
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Forage:</small>
                            <span className="fw-medium">{report.ForageStocking}</span>
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="mb-2 small">
                            <small className="text-muted d-block mb-1">Updated:</small>
                            <span className="fw-medium">{formatDateForMobile(report.updatedAt)}</span>
                          </p>
                        </div>
                        <div className="col-12 mt-2" onClick={(e) => e.stopPropagation()}>
                          <p className="mb-0 small">
                            <small className="text-muted d-block mb-1">Actions:</small>
                            <div className="donezo-row-actions">
                              <Link
                                to={"/UpdatePD/" + report.id}
                                className="btn btn-sm btn-primary"
                                title="Edit Pond Detail"
                                aria-label="Edit pond detail"
                              >
                                <i className="fa fa-edit" aria-hidden />
                              </Link>
                            </div>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="d-md-flex align-items-center justify-content-between mt-4 pt-3 donezo-fishing-data__pagination-bar">
                <div className="text-md-left text-center mb-2 mb-md-0 small text-muted">
                  Showing{" "}
                  <span className="fw-semibold text-body">
                    {pondDetails.length === 0 ? indexOfFirstEntry : indexOfFirstEntry + 1}
                  </span>{" "}
                  to{" "}
                  <span className="fw-semibold text-body">
                    {indexOfLastEntry > pondDetails.length ? pondDetails.length : indexOfLastEntry}
                  </span>{" "}
                  of <span className="fw-semibold text-body">{pondDetails.length}</span> entries
                </div>
                <ul className="pagination mb-0 justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      {windowWidth < 768 ? '«' : 'Previous'}
                    </button>
                  </li>
                  {windowWidth < 768 ? (
                    <li className="page-item active">
                      <span className="page-link">
                        {currentPage} / {totalPages}
                      </span>
                    </li>
                  ) : (
                    [...Array(totalPages)].map((_, index) => (
                      <li
                        key={index}
                        className={`page-item ${index + 1 === currentPage ? "active" : ""}`}
                      >
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => handlePageChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))
                  )}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      {windowWidth < 768 ? '»' : 'Next'}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default PondDetailViaPond;
